import { motion } from "framer-motion";
import { ArrowRight, Shield, Star, SunMedium } from "lucide-react";

import { Button } from "@/components/ui/button";

const googleColors = ["#4285F4", "#EA4335", "#FBBC05", "#4285F4", "#34A853", "#EA4335"];

const ratingItems = [
  "Heat rejection",
  "Interior UV protection",
  "Privacy with a cleaner finish",
];

export const PromoTintSection = ({ quoteUrl, tintImageUrl }) => {
  return (
    <section className="border-b border-white/10 py-20 md:py-24" data-testid="promo-tint-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          className="panel-surface luxury-outline overflow-hidden p-4 sm:p-6 lg:p-8"
          initial={{ opacity: 0, y: 32 }}
          transition={{ type: "spring", stiffness: 95, damping: 18 }}
          viewport={{ once: true, amount: 0.15 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#131313] p-5 sm:p-6" data-testid="promo-tint-visual-card">
              <div className="absolute inset-x-[18%] top-5 h-[34%] -skew-x-[26deg] rounded-[1.75rem] bg-gradient-to-r from-red-700 via-red-600 to-red-500 opacity-80 blur-[1px]" />
              <div className="absolute inset-x-[10%] bottom-[22%] h-[18%] -skew-x-[18deg] rounded-[1.75rem] bg-gradient-to-r from-rose-500 via-red-500 to-pink-500 opacity-75" />

              <div className="relative z-10 space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="section-kicker text-xs text-zinc-300" data-testid="promo-tint-kicker">
                      Window Tint
                    </p>
                    <p className="mt-2 text-sm text-zinc-300" data-testid="promo-tint-subkicker">
                      Heat Rejection & Privacy
                    </p>
                  </div>

                  <div className="glass-chip flex h-12 w-12 items-center justify-center text-white" data-testid="promo-tint-icon-chip">
                    <SunMedium className="h-5 w-5" />
                  </div>
                </div>

                <div className="relative mx-auto h-[360px] w-full max-w-[620px] overflow-hidden rounded-[1.75rem] bg-black/18" data-testid="promo-tint-image-shell">
                  <img
                    alt="Vehicle showing Detail With D window tint finish"
                    className="h-full w-full object-cover object-center drop-shadow-[0_30px_45px_rgba(0,0,0,0.45)]"
                    data-testid="promo-tint-image"
                    loading="lazy"
                    src={tintImageUrl}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm" data-testid="promo-google-rating-row">
                  <div className="flex items-center gap-1 text-[1.55rem] font-semibold leading-none" data-testid="promo-google-wordmark">
                    {"Google".split("").map((letter, index) => (
                      <span key={`${letter}-${index}`} style={{ color: googleColors[index] }}>
                        {letter}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-1 text-amber-400" data-testid="promo-google-stars">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star key={index} className="h-5 w-5 fill-current" />
                    ))}
                  </div>

                  <p className="section-kicker text-xs text-zinc-300" data-testid="promo-google-caption">
                    Trusted by Chicago drivers
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6" data-testid="promo-tint-copy-column">
              <div className="space-y-4">
                <p className="section-kicker text-sm text-blue-400" data-testid="promo-tint-section-kicker">
                  Featured tint service
                </p>
                <h2 className="max-w-3xl text-4xl font-light leading-[0.95] text-white sm:text-5xl" data-testid="promo-tint-heading">
                  Drive in style with heat-rejecting window tint built for comfort, privacy, and a sharper look.
                </h2>
                <p className="max-w-2xl text-base leading-8 text-zinc-300" data-testid="promo-tint-description">
                  Detail With D installs premium tint that helps reduce heat, protect your interior from UV exposure, and give your vehicle a clean, more finished appearance on the road.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3" data-testid="promo-tint-benefits-grid">
                {ratingItems.map((item) => (
                  <div key={item} className="glass-chip flex items-start gap-3 px-4 py-4 text-sm text-zinc-200" data-testid={`promo-tint-benefit-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                    <Shield className="mt-0.5 h-4 w-4 shrink-0 text-blue-400" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>

              <Button asChild className="glass-button glass-button-primary h-14 px-7 text-sm tracking-[0.18em] text-white transition-colors duration-300 hover:bg-blue-500">
                <a data-testid="promo-tint-quote-link" href={quoteUrl}>
                  Get a tint quote
                  <ArrowRight className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};